import * as THREE from 'three'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { FileInfo } from './file-info'
import { parseAssemblyManifestGltf } from './assembly'
import type { ImportedModelAnalysis } from './load-plan'

function resolveExplicitSemantic(
  manifestResult: ReturnType<typeof parseAssemblyManifestGltf>,
  file: FileInfo,
): ImportedModelAnalysis['explicitSemantic'] {
  const rootNode = manifestResult.manifest.nodesById[manifestResult.manifest.rootNodeId]
  const semanticKind = rootNode?.semanticKind
  if (semanticKind === 'assembly' || semanticKind === 'part') {
    return semanticKind
  }
  // Logical file kind is authoritative when the manifest is silent: a cad_assembly
  // file with no children is still an assembly (a legal insertion container), so
  // it must route through assembly-structure and return composed-assembly instead
  // of falling through to part-asset / legacy-glb.
  if (file.kind === 'cad_assembly') {
    return 'assembly'
  }
  return null
}

function canBuildPartAsset(root: THREE.Object3D): boolean {
  let hasMesh = false
  root.traverse((object) => {
    if (hasMesh) {
      return
    }
    if (object instanceof THREE.Mesh) {
      hasMesh = true
    }
  })
  return hasMesh
}

export function analyzeImportedModel(
  gltf: GLTF,
  file: FileInfo,
  version: string,
): ImportedModelAnalysis {
  const manifestResult = parseAssemblyManifestGltf(gltf, file, version)

  return {
    explicitSemantic: resolveExplicitSemantic(manifestResult, file),
    hasAssemblyManifest: Object.keys(manifestResult.manifest.nodesById).length > 0,
    referencedPartUuids: manifestResult.manifest.referencedPartUuids,
    canBuildPartAsset: canBuildPartAsset(gltf.scene),
    warnings: [...manifestResult.warnings],
  }
}
